import { useCallback, useMemo, useState } from 'react'
import { toast } from 'sonner'
import { v4 as uuidv4 } from 'uuid'

import { parseError } from '@/lib/errors'
import { DocumentType } from '@/models/document'
import { deleteFileUpload, uploadFile } from '@/models/fileUpload'

export type PendingAttachmentStatus = 'uploading' | 'uploaded' | 'error'

export interface PendingAttachment {
  clientId: string
  file: File
  status: PendingAttachmentStatus
  documentId?: string
  attachmentId?: string
  fileUrl?: string | null
  errorMessage?: string
}

export const MAX_FILES = 10
export const MAX_FILE_SIZE_BYTES = 25 * 1024 * 1024
// Email attachments stay at 10 MB — they are base64-encoded into the Gmail API
// raw message, whose total budget is 35 MB. Mirrors the backend upload view.
export const MAX_EMAIL_FILE_SIZE_BYTES = 10 * 1024 * 1024
export const MAX_TOTAL_SIZE_BYTES = 50 * 1024 * 1024

export const ACCEPTED_EXTENSIONS = [
  '.pdf',
  '.doc',
  '.docx',
  '.xlsx',
  '.xls',
  '.csv',
  '.tsv',
  '.txt',
  '.png',
  '.jpg',
  '.jpeg',
  '.gif',
  '.webp',
]

export const ACCEPTED_EXT_SET = new Set(ACCEPTED_EXTENSIONS)

function getExtension(name: string): string {
  const dot = name.lastIndexOf('.')
  if (dot === -1) return ''
  return name.slice(dot).toLowerCase()
}

export interface UseFileUploadsOptions {
  /**
   * Document type to store the upload as. Defaults to CHAT_ATTACHMENT for the
   * Ask CAI flow; pass EMAIL_ATTACHMENT (or other) for typed composers.
   */
  documentType?: DocumentType
  /**
   * File the upload under this customer rather than the uploader's own org, so
   * it reaches their documents tab and anything derived from it can link back
   * to their records. Cai staff only; the backend ignores it otherwise.
   */
  organisationId?: string
}

export function useFileUploads(options?: UseFileUploadsOptions) {
  const documentType = options?.documentType
  const organisationId = options?.organisationId
  const [attachments, setAttachments] = useState<PendingAttachment[]>([])

  const uploadedIds = useMemo(
    () =>
      attachments.filter((a) => a.status === 'uploaded' && a.documentId).map((a) => a.documentId as string),
    [attachments]
  )

  const hasUploading = useMemo(() => attachments.some((a) => a.status === 'uploading'), [attachments])

  const maxFileSizeBytes =
    documentType === DocumentType.EMAIL_ATTACHMENT ? MAX_EMAIL_FILE_SIZE_BYTES : MAX_FILE_SIZE_BYTES

  const validateAndQueue = useCallback(
    (files: File[]): PendingAttachment[] => {
      if (files.length === 0) return []

      const existing = attachments.length
      if (existing + files.length > MAX_FILES) {
        toast.error(`You can attach up to ${MAX_FILES} files per message.`)
        return []
      }

      const queued: PendingAttachment[] = []
      let runningTotal = attachments.reduce((sum, a) => sum + (a.file.size || 0), 0)

      for (const file of files) {
        const ext = getExtension(file.name)
        if (!ACCEPTED_EXT_SET.has(ext)) {
          toast.error(`File type not supported: ${file.name}`)
          continue
        }
        if (file.size > maxFileSizeBytes) {
          toast.error(`"${file.name}" is larger than ${maxFileSizeBytes / 1024 / 1024} MB.`)
          continue
        }
        runningTotal += file.size
        if (runningTotal > MAX_TOTAL_SIZE_BYTES) {
          toast.error('Total attachment size exceeds 50 MB.')
          break
        }
        queued.push({
          clientId: uuidv4(),
          file,
          status: 'uploading',
        })
      }
      return queued
    },
    [attachments, maxFileSizeBytes]
  )

  const addFiles = useCallback(
    async (files: File[] | FileList) => {
      const list = Array.from(files)
      const queued = validateAndQueue(list)
      if (queued.length === 0) return

      setAttachments((prev) => [...prev, ...queued])

      // Upload each file in parallel; update state per-file as they finish.
      await Promise.all(
        queued.map(async (pending) => {
          try {
            const result = await uploadFile(pending.file, { documentType, organisationId })
            setAttachments((prev) =>
              prev.map((a) =>
                a.clientId === pending.clientId
                  ? {
                      ...a,
                      status: 'uploaded',
                      documentId: result.documentId,
                      attachmentId: result.attachmentId,
                      fileUrl: result.fileUrl,
                    }
                  : a
              )
            )
          } catch (error) {
            const message = (() => {
              try {
                return String(parseError(error))
              } catch {
                return 'Upload failed.'
              }
            })()
            setAttachments((prev) =>
              prev.map((a) =>
                a.clientId === pending.clientId ? { ...a, status: 'error', errorMessage: message } : a
              )
            )
          }
        })
      )
    },
    [validateAndQueue, documentType, organisationId]
  )

  const removeAttachment = useCallback(async (clientId: string) => {
    let snapshot: PendingAttachment | undefined
    setAttachments((prev) => {
      snapshot = prev.find((a) => a.clientId === clientId)
      return prev.filter((a) => a.clientId !== clientId)
    })
    if (snapshot?.status === 'uploaded' && snapshot.documentId) {
      try {
        await deleteFileUpload(snapshot.documentId)
      } catch {
        // Best-effort delete; abandoned uploads get reaped by the cron.
      }
    }
  }, [])

  const clearAll = useCallback(() => {
    setAttachments([])
  }, [])

  return {
    attachments,
    uploadedIds,
    hasUploading,
    addFiles,
    removeAttachment,
    clearAll,
  }
}
